import React, { useEffect, useState } from "react";
import { PageLoading } from "../../../common/components/Loading";
import { NetworkRequest } from "../../../common/models";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import { DIRECT_MAIL_VENDORS_SLUG } from "../../surveys/sharingSurvey";
import { Api } from "../../../common/service/Api";
import { useActionRequest } from "../../../common/hooks/api";
import { parseMultiselectAnswerWithNoneOption } from "../../surveys/survey";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import type {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import {
  Modal,
  SimpleCheckbox,
  Slideout,
  StepNumberCircle,
  TextInput,
  Toggle,
  ToolTip,
} from "@truevault/aquilo";
import {
  BoltIcon,
  BoltSlashIcon,
  XMarkIcon,
  EnvelopeIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Form } from "react-final-form";
import { TRUEVAULT_BADGE_ICON } from "./DeleteInstructionsPage";
import { useSnackBar } from "@truevault/aquilo";
import clsx from "clsx";
import {
  WorkflowTableOpenCell,
  WorkflowTablePill,
  workflowTableCellStyle,
  workflowTableClasses,
  workflowTableHeaderCellStyle,
  workflowTableVendorCellStyle,
} from "./workflowSetupTable";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { VendorFlowDownEmailPreview } from "./VendorFlowDownEmailPreview";
import { RequestHandlingInstructionsTextarea } from "./RequestHandlingInstructionsTextarea";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { alphabetically } from "../../surveys/util";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { buildStepsFromWorkflowType } from "./HorizontalStepProgressIndicator";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import {
  getOptOutWorkflowVendorSelection,
  isDirectMailEligibleVendor,
  isUploadVendorForOptOutWorkflow,
  isVendorAllowedInOptOutWorkflow,
  isVendorIncludedInOptOutWorkflow,
} from "../../util/optOutWorkflowVendors";
import { getVendorNesting } from "../../util/vendorNesting";
import { filterVendorsForOptOutNotifyThirdPartiesStep } from "../../components/dataRequest/stayCompliant/steps/opt-out/NotifyThirdParties";

export function filterOptOutVendors(
  allVendors: OrganizationDataRecipientDto[],
  sharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
): {
  vendors: OrganizationDataRecipientDto[];
  customAudienceVendorManagers: string[];
  managedAdNetworksIds: string[];
  getManagementVendorForNetwork: (networkId: string) => OrganizationDataRecipientDto | null;
  getManagedVendorsForManager: (dataRecipientId: string) => OrganizationDataRecipientDto[];
} {
  const nesting = getVendorNesting(allVendors, managedAdNetworks, sharingAnswers, {
    isManagerEligible: isVendorAllowedInOptOutWorkflow,
  });
  const {
    customAudienceManagerIds: customAudienceVendorManagers,
    managedVendorIds: managedAdNetworksIds,
    activeManagers: managementVendors,
    getManagerForManagedVendor: getManagementVendorForNetwork,
    getManagedVendorsForManager,
  } = nesting;

  let vendors = allVendors?.filter((v) => {
    if (!isVendorAllowedInOptOutWorkflow(v)) {
      return false;
    }

    // Vendors nested under a manager are handled there, so they are not listed separately.
    if (nesting.isManaged(v.id)) {
      return false;
    }

    // Ensure vendor has associated opt-out instruction
    let optOutInstruction = v.instructions?.find((vi) => vi.requestType == "OPT_OUT");
    if (!optOutInstruction) {
      return false;
    }

    return (
      isUploadVendorForOptOutWorkflow(v) ||
      v.usesCustomAudience ||
      v.ccpaIsSelling ||
      v.isAlwaysDataSellingVendor ||
      v.ccpaIsSharing
    );
  });

  // Add management vendors that should appear in opt-out instructions
  if (managementVendors.length > 0 && managedAdNetworksIds.length > 0) {
    const managementVendorsToAdd = managementVendors.filter((vendor) => {
      // Skip if already in vendors list
      if (vendors?.find((v) => v.id === vendor.id)) {
        return false;
      }

      // Include accessible vendors (current behavior)
      if (vendor.dataAccessibility === "ACCESSIBLE") {
        return true;
      }

      // Include inaccessible vendors that don't have an email (new requirement)
      if (vendor.dataAccessibility === "NOT_ACCESSIBLE" && !vendor.email && !vendor.defaultEmail) {
        return true;
      }

      return false;
    });

    if (managementVendorsToAdd.length > 0) {
      vendors?.push(...managementVendorsToAdd);
    }
  }
  vendors?.sort(alphabetically);

  return {
    vendors,
    managedAdNetworksIds,
    customAudienceVendorManagers,
    getManagementVendorForNetwork,
    getManagedVendorsForManager,
  };
}

type OptOutInstructionsPageProps = {
  collectionContext?: CollectionContext[];
  embedded?: boolean;
};

export const OptOutInstructionsPage = ({
  collectionContext,
  embedded = false,
}: OptOutInstructionsPageProps = {}) => {
  const history = useHistory();
  const snackbar = useSnackBar();
  // Constants

  // State
  const [readyToRender, setReadyToRender] = useState(false);
  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  // Requests
  const [organization, orgRequest, refreshOrg] = usePrimaryOrganization();
  const [allVendors, vendorsRequest, fetchVendors] = useOrganizationVendors(
    organization?.id,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ collectionContext,
    /* onlyUseSettled = */ true,
  );

  const [
    autoSendRequestEmailNotificationsForOptOutRequestsValue,
    setAutoSendRequestEmailNotificationsForOptOutRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForOptOutRequestsValue(
        organization.autoSendRequestEmailNotificationsForOptOutRequests,
      );
    }
  }, [organization]);

  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);
  const [, instructionsRequest, fetchInstructions] = useRequestInstructions(
    organization.id,
    "OPT_OUT",
    (data: RequestHandlingInstructionDto[]) => {
      setInstructions(data);
    },
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(organization?.id, newInstruction);
    },
    onSuccess: fetchInstructions,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    // },
  });

  const {
    answers: sharingAnswers,
    setAnswer: setSharingAnswer,
    request: sharingSurveyRequest,
  } = useSurvey(organization.id, SELLING_AND_SHARING_SURVEY_NAME);

  // Determines whether to show the direct mail checkbox in the vendor sidebar
  // Initialized based on the presence or absence of any existing direct mail vendors
  const [enableDirectMailToggle, setEnableDirectMailToggle] = useState(null);
  useEffect(() => {
    if (sharingAnswers && enableDirectMailToggle === null) {
      const directMailVendorIds = parseMultiselectAnswerWithNoneOption(
        sharingAnswers,
        DIRECT_MAIL_VENDORS_SLUG,
      );
      setEnableDirectMailToggle(directMailVendorIds.length > 0);
    }
  }, [sharingAnswers, enableDirectMailToggle]);
  const [showDirectMailDisableConfirmationModal, setShowDirectMailDisableConfirmationModal] =
    useState(false);

  const { settings: apiIntegrations } = useIntegrationsSettings();

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(organization?.id, vendorId, vendorSettings);
    },
    onSuccess: fetchVendors,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor data accessibility settings`, { variant: "error" });
    // },
  });

  const { managedAdNetworks } = useManagedAdNetworks(organization?.id);

  const {
    vendors,
    managedAdNetworksIds,
    getManagementVendorForNetwork,
    getManagedVendorsForManager,
  } = filterOptOutVendors(allVendors, sharingAnswers, managedAdNetworks);

  // Keep track of contact row column completion for enabling/disabling automation toggle
  const [dataRecipientContactEmailCompletionById, setDataRecipientContactEmailCompletionById] =
    useState<Map<string, boolean>>(null);

  // Initialize dataRecipientContactEmailCompletionById
  useEffect(() => {
    if (
      !allVendors ||
      allVendors.length == 0 ||
      !managedAdNetworks ||
      !sharingAnswers ||
      !instructions
    ) {
      return;
    }

    if (dataRecipientContactEmailCompletionById === null) {
      // Direct-mail selections can change without reloading the page, so initialize their
      // contact state too. Only currently eligible vendors are checked below.
      let initMap = new Map<string, boolean>(
        allVendors.map((dr) => {
          let optOutInstruction = instructions?.find(
            (di) => di.vendorId == dr.vendorId && di.requestType == "OPT_OUT",
          );
          return [dr.id, optOutInstruction && isRowContactEmailCompleted(dr, optOutInstruction)];
        }),
      );
      setDataRecipientContactEmailCompletionById(initMap);
    }
  }, [
    allVendors,
    managedAdNetworks,
    sharingAnswers,
    instructions,
    dataRecipientContactEmailCompletionById,
  ]);

  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest, instructionsRequest, vendorsRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest, instructionsRequest, vendorsRequest]);

  if (
    !readyToRender ||
    !organization ||
    vendors === undefined ||
    !NetworkRequest.areFinished(sharingSurveyRequest) ||
    dataRecipientContactEmailCompletionById === null
  ) {
    if (embedded) {
      return <PageLoading />;
    }

    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  const optOutWorkflowSelection = getOptOutWorkflowVendorSelection(
    allVendors,
    sharingAnswers,
    managedAdNetworks,
  );
  const directMailVendorIds = optOutWorkflowSelection.directMailVendorIds;
  const allCandidateVendorsForAutomation = [
    ...vendors,
    ...allVendors.filter(
      (vendor) =>
        directMailVendorIds.includes(vendor.id) &&
        isVendorIncludedInOptOutWorkflow(vendor, optOutWorkflowSelection) &&
        !vendors.some((v) => v.id === vendor.id),
    ),
  ];

  if (allCandidateVendorsForAutomation.length === 0) {
    const emptyState = (
      <div className="tw-boundary">
        <div className="text-neutral-600 font-semibold mt-12 max-w-[776px]">
          Based on your data map you do not require an opt-out workflow. Your opt-outs are performed
          automatically on your website and logged in your closed requests.
        </div>
      </div>
    );

    if (embedded) {
      return emptyState;
    }

    return (
      <WorkflowSetupPageWrapper
        selectedTabKey="request-to-opt-out"
        nextOnClick={async () => {
          history.push(Routes.organization.requestHandling.accessRequest);
        }}
        nextDisabled={false}
      >
        {emptyState}
      </WorkflowSetupPageWrapper>
    );
  }

  let missingContactEmailVendorNames = allCandidateVendorsForAutomation
    .filter((v) => dataRecipientContactEmailCompletionById.get(v.id) !== true)
    .map((v) => v.name);
  let isAutomationToggleDisabled = missingContactEmailVendorNames.length > 0;
  let isAutomationToggleDisabledTooltipText = `Missing contact email for: ${missingContactEmailVendorNames.join(
    ", ",
  )}`;

  const vendorsForRemoveConsumer = vendors.filter(
    (vendor) => vendor.dataAccessibility !== "NOT_ACCESSIBLE",
  );

  let vendorsToContact = filterVendorsForOptOutNotifyThirdPartiesStep(
    allVendors,
    /* request = */ null,
    /* dataMap = */ null,
    sharingAnswers,
    managedAdNetworks,
    organization,
  );
  vendors
    .filter((vendor) => vendor.dataAccessibility === "NOT_ACCESSIBLE")
    .forEach((vendor) => {
      if (!vendorsToContact.some((candidate) => candidate.id === vendor.id)) {
        vendorsToContact.push(vendor);
      }
    });
  vendorsToContact.sort(alphabetically);

  let updateIncludedInDirectMail = async (
    dataRecipient: OrganizationDataRecipientDto,
    isIncluded: boolean,
  ) => {
    let newDirectMailVendorIds = new Set(directMailVendorIds);
    if (isIncluded) {
      newDirectMailVendorIds.add(dataRecipient.id);
    } else {
      newDirectMailVendorIds.delete(dataRecipient.id);
    }
    await setSharingAnswer(
      DIRECT_MAIL_VENDORS_SLUG,
      JSON.stringify(Array.from(newDirectMailVendorIds)),
    );
  };

  // Helper to check if a vendor is automated or not accessible
  const isVendorAutomatedOrNotAccessible = (vendor: OrganizationDataRecipientDto) => {
    // Vendor is "automated" if it has a connected API integration WITH opt-out workflow enabled
    const vendorApiConnection = (apiIntegrations?.vendors || []).find(
      (apiIntegration) => apiIntegration.vendorId === vendor.vendorId,
    );
    const isAutomated =
      vendorApiConnection?.connected === true && vendorApiConnection?.optOutEnabled === true;
    // Vendor is "not accessible" if it can't be accessed for automation
    const isNotAccessible = vendor.dataAccessibility === "NOT_ACCESSIBLE";
    return isAutomated || isNotAccessible;
  };

  const isFullyAutomated = allCandidateVendorsForAutomation.every((vendor) => {
    if (!isVendorIncludedInOptOutWorkflow(vendor, optOutWorkflowSelection)) {
      return true;
    }
    return isVendorAutomatedOrNotAccessible(vendor);
  });

  const showFullyAutomatedCallout =
    isFullyAutomated && autoSendRequestEmailNotificationsForOptOutRequestsValue;
  const showEnableAutomationCallout =
    isFullyAutomated && !autoSendRequestEmailNotificationsForOptOutRequestsValue;
  const optOutStepProgressSteps = buildStepsFromWorkflowType(WorkflowType.OPT_OUT, 1);
  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };

  const fullyAutomatedCallout = showFullyAutomatedCallout ? (
    <div className="flex items-center mb-6">
      <div>
        <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl mr-3" />
      </div>
      <div className="text-green-700 font-medium text-sm">
        Opt-Out requests processing is fully automated. Closed requests will show up in your
        &ldquo;Closed&rdquo; inbox tab.
      </div>
    </div>
  ) : null;

  const pageContent = (
    <>
      <div className="tw-boundary">
        <div className="max-w-[924px]">
          {showEnableAutomationCallout && (
            <div className="bg-blue-50 flex items-center max-w-[924px] mb-6 p-4 rounded-lg">
              <div>
                <InformationCircleIcon className="w-4 h-4 text-blue-600 mr-3" />
              </div>
              <div className="text-blue-700 font-medium text-sm">
                Toggle automation "on" in Step 3 below to fully automate the opt-out request
                workflow.
              </div>
            </div>
          )}
          <div id="remove-consumer" className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="1" />
            <div className="text-neutral-900 text-[18px] font-normal">
              Review vendors and their opt-out instructions
            </div>
          </div>

          <div className="mt-1.5 ml-[40px]">
            <OptOutInstructionsTable
              vendors={vendorsForRemoveConsumer}
              tableId="instructions"
              tableType="vendors"
              showContactForRequestsColumn={false}
              updateDataRecipient={updateDataRecipient}
              instructions={instructions}
              updateInstruction={updateInstruction}
              sidebarOpenForVendorId={sidebarOpenForVendorId}
              setSidebarOpenForVendorId={setSidebarOpenForVendorId}
              apiIntegrations={apiIntegrations}
              setDataRecipientContactEmailCompletionById={
                setDataRecipientContactEmailCompletionById
              }
              managedAdNetworksIds={managedAdNetworksIds}
              getManagementVendorForNetwork={getManagementVendorForNetwork}
              getManagedVendorsForManager={getManagedVendorsForManager}
              directMailVendorIds={directMailVendorIds}
              updateIncludedInDirectMail={updateIncludedInDirectMail}
            />
          </div>

          <div className="mt-[24px] ml-[40px]">
            <div className="flex gap-[12px] items-center">
              <div className="w-[20px] h-[20px] rounded-[100px] bg-primary-100 flex items-center justify-center">
                <EnvelopeIcon className="text-primary-500 w-[14px] h-[14px]" />
              </div>
              <div className="text-neutral-900 text-[14px] font-normal">
                Optional: include direct mail vendors in opt-out processing
              </div>
              <ToolTip
                text={
                  "Privacy opt-outs apply only to targeted online advertising, but many businesses choose to also remove consumers from promotional emails and mailers. Consumers may expect to be removed from such outreach when they exercise their opt-out right. Adjust vendor settings below. These settings can be changed at any time."
                }
                width="230px"
              >
                <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
              </ToolTip>
              <Toggle
                checked={enableDirectMailToggle}
                onChange={async (newChecked: boolean) => {
                  if (newChecked === false && directMailVendorIds.length > 0) {
                    setShowDirectMailDisableConfirmationModal(true);
                  } else if (newChecked === false) {
                    await setSharingAnswer(DIRECT_MAIL_VENDORS_SLUG, "[]");
                    setEnableDirectMailToggle(false);
                  } else if (newChecked === true) {
                    setEnableDirectMailToggle(true);
                  }
                }}
              />
            </div>
            {showDirectMailDisableConfirmationModal && (
              <DirectMailDisableConfirmationModal
                closeModal={() => setShowDirectMailDisableConfirmationModal(false)}
                onConfirm={async () => {
                  await setSharingAnswer(DIRECT_MAIL_VENDORS_SLUG, "[]");
                  setEnableDirectMailToggle(false);
                  setShowDirectMailDisableConfirmationModal(false);
                }}
              />
            )}
          </div>

          {enableDirectMailToggle && (
            <div className="mt-[12px] ml-[40px]">
              <OptOutInstructionsTable
                vendors={allVendors.filter((vendor) => {
                  if (!isVendorAllowedInOptOutWorkflow(vendor)) {
                    return false;
                  }

                  if (!isDirectMailEligibleVendor(vendor)) {
                    return false;
                  }

                  // Don't show vendors that are already shown in the above vendors table
                  if (vendors.find((v) => v.id == vendor.id)) {
                    return false;
                  }

                  return true;
                })}
                tableId="direct-mail"
                tableType="direct-mail-vendors"
                updateDataRecipient={updateDataRecipient}
                instructions={instructions}
                updateInstruction={updateInstruction}
                sidebarOpenForVendorId={sidebarOpenForVendorId}
                setSidebarOpenForVendorId={setSidebarOpenForVendorId}
                apiIntegrations={apiIntegrations}
                setDataRecipientContactEmailCompletionById={
                  setDataRecipientContactEmailCompletionById
                }
                managedAdNetworksIds={managedAdNetworksIds}
                getManagementVendorForNetwork={getManagementVendorForNetwork}
                getManagedVendorsForManager={getManagedVendorsForManager}
                directMailVendorIds={directMailVendorIds}
                updateIncludedInDirectMail={updateIncludedInDirectMail}
              />
            </div>
          )}
        </div>

        <div id="contact-vendors" className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="2" />
          <div className="text-gray-900 text-[18px] font-normal">
            Review your vendors who will be contacted for opt-out requests via email
          </div>
        </div>
        <div className="mt-1.5 ml-[40px]">
          {vendorsToContact.length === 0 ? (
            <div className="text-neutral-800 text-[14px] italic">
              No vendors need to be contacted via email for opt-out requests.
            </div>
          ) : (
            <OptOutInstructionsTable
              vendors={vendorsToContact}
              tableId="contact-vendors"
              tableType="vendors"
              showOptOutInstructionsColumn={false}
              updateDataRecipient={updateDataRecipient}
              instructions={instructions}
              updateInstruction={updateInstruction}
              sidebarOpenForVendorId={sidebarOpenForVendorId}
              setSidebarOpenForVendorId={setSidebarOpenForVendorId}
              apiIntegrations={apiIntegrations}
              setDataRecipientContactEmailCompletionById={
                setDataRecipientContactEmailCompletionById
              }
              managedAdNetworksIds={managedAdNetworksIds}
              getManagementVendorForNetwork={getManagementVendorForNetwork}
              getManagedVendorsForManager={getManagedVendorsForManager}
              directMailVendorIds={directMailVendorIds}
              updateIncludedInDirectMail={updateIncludedInDirectMail}
            />
          )}
        </div>
        <div className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="3" />
          <div className="text-gray-900 text-[18px] font-normal">
            Auto-send request processing emails
          </div>
        </div>
        <div className="ml-[40px] mt-[6px]">
          <ToolTip text={isAutomationToggleDisabled ? isAutomationToggleDisabledTooltipText : null}>
            <span className="flex items-center gap-2">
              <span className="text-neutral-900 text-sm font-normal">Automate this step</span>
              <Toggle
                // Even if the automation setting is set to ON, if it becomes disabled (because of e.g. a new vendor was added with a missing contact email), then the automation won't work anyways, so show the automation toggle as OFF whenever it's disabled
                checked={
                  isAutomationToggleDisabled
                    ? false
                    : autoSendRequestEmailNotificationsForOptOutRequestsValue
                }
                disabled={isAutomationToggleDisabled}
                onChange={async (checked) => {
                  setAutoSendRequestEmailNotificationsForOptOutRequestsValue(checked);
                  await Api.organization.updateOrganization(organization.id, {
                    name: organization.name,
                    logoUrl: organization.logoUrl,
                    regulations: [],
                    autoSendRequestEmailNotificationsForOptOutRequests: checked,
                  });
                  await refreshOrg();

                  if (checked) {
                    snackbar("Enabled automatically sending opt-out request email notifications", {
                      variant: "success",
                    });
                  } else {
                    // closeSnackbar functionality not available in new API
                  }
                }}
              />
            </span>
          </ToolTip>
        </div>
        <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
          If this is toggled off, you will be prompted to manually click a button to send the vendor
          email and consumer notification email below for each request you process.
        </div>
      </div>

      <div style={{ marginTop: "12px", marginLeft: "40px" }}>
        <VendorFlowDownEmailPreview organizationId={organization.id} requestType="opt-out" />
      </div>
    </>
  );

  if (embedded) {
    return (
      <>
        {fullyAutomatedCallout}
        {pageContent}
      </>
    );
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-opt-out"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.accessRequest);
      }}
      nextDisabled={false}
      hideFooter={sidebarOpenForVendorId != null}
      contentAboveSteps={fullyAutomatedCallout}
      stepProgressSteps={optOutStepProgressSteps}
      onStepProgressClick={handleStepProgressClick}
    >
      {pageContent}
    </WorkflowSetupPageWrapper>
  );
};

function OptOutInstructionsTable({
  vendors,
  tableId,
  tableType,
  showContactForRequestsColumn = true,
  showOptOutInstructionsColumn = true,
  updateDataRecipient,
  instructions,
  updateInstruction,
  updateIncludedInDirectMail,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  apiIntegrations,
  setDataRecipientContactEmailCompletionById,
  directMailVendorIds,
  managedAdNetworksIds,
  getManagementVendorForNetwork,
  getManagedVendorsForManager,
}: {
  vendors: OrganizationDataRecipientDto[];
  tableId: "instructions" | "direct-mail" | "contact-vendors";
  tableType: "vendors" | "direct-mail-vendors";
  showContactForRequestsColumn?: boolean;
  showOptOutInstructionsColumn?: boolean;
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  instructions: RequestHandlingInstructionDto[];
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  updateIncludedInDirectMail: (
    dataRecipient: OrganizationDataRecipientDto,
    isIncluded: boolean,
  ) => Promise<void>;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: (vendorId: string | null) => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  setDataRecipientContactEmailCompletionById: React.Dispatch<
    React.SetStateAction<Map<string, boolean>>
  >;
  directMailVendorIds: string[];
  managedAdNetworksIds: string[];
  getManagementVendorForNetwork: (networkId: string) => OrganizationDataRecipientDto | null;
  getManagedVendorsForManager: (dataRecipientId: string) => OrganizationDataRecipientDto[];
}) {
  const contactColumnWidth = showOptOutInstructionsColumn ? 188 : 338;
  const optOutInstructionsColumnWidth = 338;
  const tableWidth =
    338 +
    (showContactForRequestsColumn ? contactColumnWidth : 0) +
    (showOptOutInstructionsColumn ? optOutInstructionsColumnWidth : 0) +
    60;

  return (
    <div className={workflowTableClasses.container} style={{ maxWidth: `${tableWidth}px` }}>
      <table
        className="w-full table-fixed"
        style={{ borderCollapse: "separate", borderSpacing: 0 }}
      >
        <colgroup>
          <col style={{ width: "338px" }} />
          {showContactForRequestsColumn && (
            <col style={showOptOutInstructionsColumn ? { width: "188px" } : undefined} />
          )}
          {showOptOutInstructionsColumn && <col />}
          <col style={{ width: "60px" }} />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            <th
              scope="col"
              className={clsx(workflowTableClasses.headerCell, "pl-6 text-left")}
              style={workflowTableHeaderCellStyle}
            >
              Vendor
            </th>
            {showContactForRequestsColumn && (
              <th
                scope="col"
                className={clsx(workflowTableClasses.headerCell, "text-center")}
                style={workflowTableHeaderCellStyle}
              >
                {tableType === "vendors" ? "Contact for Requests" : "Add To Opt-Out Workflow"}
              </th>
            )}
            {showOptOutInstructionsColumn && (
              <th
                scope="col"
                className={clsx(workflowTableClasses.headerCell, "text-center")}
                style={workflowTableHeaderCellStyle}
              >
                Opt Out Instructions
              </th>
            )}
            <th
              scope="col"
              className={workflowTableClasses.headerCell}
              style={workflowTableHeaderCellStyle}
            >
              &nbsp;
            </th>
          </tr>
        </thead>
        <tbody>
          {vendors?.map((dataRecipient) => {
            const sidebarId = `${tableId}:${dataRecipient.vendorId}`;
            const optOutInstruction = instructions?.find(
              (instruction) =>
                instruction.vendorId == dataRecipient.vendorId &&
                instruction.requestType == "OPT_OUT",
            );

            if (!optOutInstruction) {
              return null;
            }

            const isManaged = managedAdNetworksIds.includes(dataRecipient.id);
            const managingVendor = isManaged
              ? getManagementVendorForNetwork(dataRecipient.id)
              : null;

            return (
              <DataRecipientRowWithSidebar
                key={dataRecipient.id}
                dataRecipient={dataRecipient}
                tableType={tableType}
                showContactForRequestsColumn={showContactForRequestsColumn}
                showOptOutInstructionsColumn={showOptOutInstructionsColumn}
                optOutInstruction={optOutInstruction}
                updateDataRecipient={updateDataRecipient}
                updateInstruction={updateInstruction}
                openSidebar={() => setSidebarOpenForVendorId(sidebarId)}
                isSidebarOpen={sidebarOpenForVendorId === sidebarId}
                closeSidebar={() => setSidebarOpenForVendorId(null)}
                apiIntegrations={apiIntegrations}
                managingVendor={managingVendor}
                getManagedVendorsForManager={getManagedVendorsForManager}
                setContactEmailCompletionForDataRecipient={(
                  drId: string,
                  contactEmailCompleted: boolean,
                ) => {
                  setDataRecipientContactEmailCompletionById((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(drId, contactEmailCompleted);
                    return newMap;
                  });
                }}
                isIncludedInDirectMail={directMailVendorIds.includes(dataRecipient.id)}
                enableDirectMailToggle={
                  tableType === "direct-mail-vendors" ||
                  directMailVendorIds.includes(dataRecipient.id)
                }
                updateIncludedInDirectMail={updateIncludedInDirectMail}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DataRecipientRowWithSidebar({
  dataRecipient,
  optOutInstruction,
  updateDataRecipient,
  updateInstruction,
  tableType,
  showContactForRequestsColumn,
  showOptOutInstructionsColumn,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  apiIntegrations,
  setContactEmailCompletionForDataRecipient,
  managingVendor,
  getManagedVendorsForManager,
  enableDirectMailToggle,
  isIncludedInDirectMail,
  updateIncludedInDirectMail,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  optOutInstruction: RequestHandlingInstructionDto;
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  tableType: "vendors" | "direct-mail-vendors";
  showContactForRequestsColumn: boolean;
  showOptOutInstructionsColumn: boolean;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  setContactEmailCompletionForDataRecipient: (id: string, contactEmailCompleted: boolean) => void;
  managingVendor: OrganizationDataRecipientDto | null;
  getManagedVendorsForManager: (dataRecipientId: string) => OrganizationDataRecipientDto[];
  enableDirectMailToggle: boolean;
  isIncludedInDirectMail: boolean;
  updateIncludedInDirectMail: (
    vendor: OrganizationDataRecipientDto,
    included: boolean,
  ) => Promise<void>;
}) {
  const [email, setEmail] = useState(dataRecipient.email || dataRecipient.defaultEmail);
  const [doNotContactValue, setDoNotContactValue] = useState(optOutInstruction.doNotContact);
  const [userProvidedOptOutInstructions, setUserProvidedOptOutInstructions] = useState(
    optOutInstruction.processingInstructions,
  );

  const dataRecipientWithLocalChanges = {
    ...dataRecipient,
  };
  // only set email if it is not empty
  if (email && email.length > 0) {
    dataRecipientWithLocalChanges.email = email;
  }

  const optOutInstructionWithLocalChanges: RequestHandlingInstructionDto = {
    ...optOutInstruction,
    doNotContact: doNotContactValue,
    processingInstructions: userProvidedOptOutInstructions,
  };

  let hasVendorOptOutInstructions = dataRecipientWithLocalChanges.optOutInstructions?.length > 0;

  // If this vendor is managed, use the managing vendor's instructions
  const effectiveVendor = managingVendor || dataRecipientWithLocalChanges;
  const effectiveOptOutInstructions = effectiveVendor?.optOutInstructions;
  hasVendorOptOutInstructions = effectiveOptOutInstructions?.length > 0;

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipientWithLocalChanges.id}
        className={clsx(workflowTableClasses.row, "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td
          className={clsx(workflowTableClasses.cell, "w-[338px] max-w-[338px]")}
          style={workflowTableVendorCellStyle}
        >
          <div>
            <div className="flex items-center gap-[12px]">
              <img
                src={dataRecipientWithLocalChanges.logoUrl}
                alt={dataRecipientWithLocalChanges.name}
                className="h-6 w-6"
              />
              <div className="flex flex-col">
                <span className={workflowTableClasses.vendorName}>
                  {dataRecipientWithLocalChanges.name}
                </span>
                {managingVendor && (
                  <span className="text-gray-500 text-xs">(managed by {managingVendor.name})</span>
                )}
              </div>
              {vendorApiConnection &&
                (vendorApiConnection.connected ? (
                  <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
                ) : (
                  <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
                ))}
            </div>
            {(() => {
              // Show which vendors are nested under this vendor (only if it is an active manager)
              const networksManaged = getManagedVendorsForManager(dataRecipientWithLocalChanges.id);

              if (networksManaged.length === 0) return null;

              return (
                <div className="mt-2">
                  <div className="text-gray-600 text-xs font-medium">
                    Managing: {networksManaged.map((network) => network?.name).join(", ")}
                  </div>
                </div>
              );
            })()}
          </div>
        </td>
        {showContactForRequestsColumn && (
          <td
            className={clsx(workflowTableClasses.cell, "text-center")}
            style={{ ...workflowTableCellStyle, paddingLeft: 12, paddingRight: 12 }}
          >
            {tableType === "vendors"
              ? contactForRequestsColumnValue(
                  dataRecipientWithLocalChanges,
                  optOutInstructionWithLocalChanges,
                )
              : directMailColumnValue(dataRecipientWithLocalChanges, isIncludedInDirectMail)}
          </td>
        )}
        {showOptOutInstructionsColumn && (
          <td
            className={clsx(workflowTableClasses.cell, "text-center")}
            style={{ ...workflowTableCellStyle, paddingLeft: 12, paddingRight: 12 }}
          >
            {optOutInstructionsColumnValue(
              dataRecipientWithLocalChanges,
              optOutInstructionWithLocalChanges,
              managingVendor,
              vendorApiConnection,
            )}
          </td>
        )}
        <WorkflowTableOpenCell
          onOpen={openSidebar}
          vendorName={dataRecipientWithLocalChanges.name}
        />
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-gray-900 text-[18px] font-bold">
                Request to Opt-Out Vendor Settings
              </h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center">
                <img
                  src={dataRecipientWithLocalChanges.logoUrl}
                  alt={dataRecipientWithLocalChanges.name}
                  className="h-5 w-5"
                />
                <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px]">
                  {dataRecipientWithLocalChanges.name}
                </span>
                {dataRecipientWithLocalChanges.dataAccessibility === "NOT_ACCESSIBLE" && (
                  <div className="bg-neutral-200 rounded-[6px] py-[4px] px-[8px] flex items-center">
                    <span className="text-gray-800 text-[12px] font-medium">Not Accessible</span>
                  </div>
                )}
                {vendorApiConnection && vendorApiConnection.connected && (
                  <AutomationConnectedBadge />
                )}
              </div>
              {dataRecipientWithLocalChanges.url?.length > 0 && (
                <div className="mt-1 text-[14px] font-normal text-gray-500">
                  {dataRecipientWithLocalChanges.url}
                </div>
              )}
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="p-6">
                      {dataRecipientWithLocalChanges.dataAccessibility === "NOT_ACCESSIBLE" &&
                        !email && (
                          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                            <p className="text-sm text-amber-800">
                              This vendor is not accessible for automated opt-outs. Please add a
                              contact email address so you can manually send opt-out requests when
                              needed.
                            </p>
                          </div>
                        )}
                      <div>
                        <label className="block text-[14px] font-medium text-gray-700">
                          Contact Email for Requests
                        </label>
                        <TextInput
                          field="vendor-opt-out-contact-email"
                          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                            let newEmail = e.target.value;
                            setEmail(newEmail);
                            let updatedDataRecipient = {
                              ...dataRecipientWithLocalChanges,
                              email: newEmail,
                            };
                            await updateDataRecipient({
                              vendorId: dataRecipientWithLocalChanges.vendorId,
                              vendorSettings: {
                                ...updatedDataRecipient,
                                classificationId: dataRecipientWithLocalChanges.classification.id,
                              },
                            });

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                updatedDataRecipient,
                                optOutInstructionWithLocalChanges,
                              ),
                            );
                          }}
                          defaultValue={email}
                          maxLength={90}
                          disabled={doNotContactValue}
                        />
                      </div>
                      <div className="mt-6">
                        <SimpleCheckbox
                          name="doNotContact"
                          label={`Do Not Contact ${dataRecipient.name} for Opt-Out Requests`}
                          initialChecked={optOutInstruction?.doNotContact}
                          onChange={async () => {
                            let newDoNotContact = !doNotContactValue;

                            setDoNotContactValue(newDoNotContact);

                            let updatedInstruction = {
                              ...optOutInstructionWithLocalChanges,
                              doNotContact: newDoNotContact,
                            };

                            await updateInstruction(updatedInstruction);

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                dataRecipientWithLocalChanges,
                                updatedInstruction,
                              ),
                            );
                          }}
                        />
                      </div>
                    </div>
                    <hr className="border-gray-200" />
                    {enableDirectMailToggle && (
                      <>
                        <div className="p-6">
                          <SimpleCheckbox
                            name="includeInDirectMail"
                            label={`Include vendor in direct mail opt-out process`}
                            initialChecked={isIncludedInDirectMail}
                            onChange={async (newChecked: boolean) => {
                              await updateIncludedInDirectMail(
                                dataRecipientWithLocalChanges,
                                newChecked,
                              );
                            }}
                          />
                        </div>
                        <hr className="border-gray-200" />
                      </>
                    )}
                    <div className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-[14px] text-gray-700 font-medium">
                          {managingVendor
                            ? `Self Service Opt-Out Instructions (via ${managingVendor.name})`
                            : "Self Service Opt-Out Instructions"}
                        </div>
                        {hasVendorOptOutInstructions && (
                          <div className="flex items-center gap-1">
                            {TRUEVAULT_BADGE_ICON}
                            <span className="text-primary-600 text-[10px] font-medium">
                              Verified
                            </span>
                          </div>
                        )}
                      </div>
                      {hasVendorOptOutInstructions && (
                        <div className="vendor-instructions mt-3">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: effectiveOptOutInstructions,
                            }}
                          />
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-neutral-700 text-[14px] font-medium">
                          {hasVendorOptOutInstructions
                            ? "Add Your Opt-Out Instructions (optional)"
                            : "Add Your Opt-Out Instructions"}
                        </div>
                        <RequestHandlingInstructionsTextarea
                          rows={4}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                          defaultValue={userProvidedOptOutInstructions}
                          placeholder="E.g. find the consumer first at the settings.."
                          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                            let newUserProvidedOptOutInstructions = e.target.value;

                            setUserProvidedOptOutInstructions(newUserProvidedOptOutInstructions);

                            let updatedInstruction = {
                              ...optOutInstructionWithLocalChanges,
                              processingInstructions: newUserProvidedOptOutInstructions,
                            };

                            await updateInstruction(updatedInstruction);

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                dataRecipientWithLocalChanges,
                                updatedInstruction,
                              ),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </>
      </Slideout>
    </>
  );
}

function DirectMailDisableConfirmationModal({
  closeModal,
  onConfirm,
}: {
  closeModal: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Modal
      title="Are you sure you want to disable direct mail vendors in opt-out processing?"
      alignText="left"
      firstButton="Cancel"
      firstButtonColor="tertiary"
      onFirstButtonClick={closeModal}
      alignButton="right"
      secondButton="OK"
      secondButtonColor="primary"
      onSecondButtonClick={onConfirm}
      showOverlay
      closeOnOverlayClick={false}
      onClose={closeModal}
    >
      <p className="text-gray-500 text-[14px] font-normal mt-2 leading-[20px]">
        This will clear the list of vendors included in direct mail opt-out processing. If you want
        to re-enable direct mail vendors, you will need to add them again.
      </p>
    </Modal>
  );
}

const contactForRequestsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";
  let email = vendor.email || vendor.defaultEmail;

  if (optOutInstruction?.doNotContact) {
    resultLabel = "Do Not Contact";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else if (email) {
    resultLabel = email;
    textColor = "text-blue-800";
    bgColor = "bg-blue-100";
  } else {
    resultLabel = "Email Address Needed";
    textColor = "text-amber-800";
    bgColor = "bg-amber-100";
  }

  return <WorkflowTablePill className={clsx(textColor, bgColor)}>{resultLabel}</WorkflowTablePill>;
};

const directMailColumnValue = (
  vendor: OrganizationDataRecipientDto,
  isIncludedInDirectMail: boolean,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";

  if (isIncludedInDirectMail) {
    resultLabel = "Yes";
    textColor = "text-blue-800";
    bgColor = "bg-blue-100";
  } else {
    resultLabel = "No";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  }

  return <WorkflowTablePill className={clsx(textColor, bgColor)}>{resultLabel}</WorkflowTablePill>;
};

const optOutInstructionsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
  managingVendor?: OrganizationDataRecipientDto | null,
  vendorApiConnection?: { connected: boolean; optOutEnabled: boolean } | null,
): React.ReactNode => {
  let resultLabel: React.ReactNode = "",
    textColor = "",
    bgColor = "";

  // Check if vendor has active opt-out automation (highest priority)
  const isAutomatedForOptOut =
    vendorApiConnection?.connected === true && vendorApiConnection?.optOutEnabled === true;

  // If this vendor is managed, show the managing vendor's instructions instead
  const effectiveVendor = managingVendor || vendor;
  const hasOptOutInstructions = effectiveVendor?.optOutInstructions?.length > 0;

  if (isAutomatedForOptOut) {
    resultLabel = "Automated";
    textColor = "text-green-700";
    bgColor = "bg-green-100";
  } else if (hasOptOutInstructions) {
    resultLabel = (
      <div className="flex items-center gap-1">
        {TRUEVAULT_BADGE_ICON}
        <span className="text-primary-600">
          {managingVendor ? `Verified (via ${managingVendor.name})` : "Verified"}
        </span>
      </div>
    );
    bgColor = "bg-[#EDE9FE]";
  } else if (optOutInstruction?.processingInstructions) {
    resultLabel = "Your Instructions";
    textColor = "text-blue-800";
    bgColor = "bg-blue-100";
  } else if (vendor.dataAccessibility === "NOT_ACCESSIBLE") {
    resultLabel = "Not Accessible";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else {
    resultLabel = "Add Instructions";
    textColor = "text-amber-800";
    bgColor = "bg-amber-100";
  }

  return <WorkflowTablePill className={clsx(textColor, bgColor)}>{resultLabel}</WorkflowTablePill>;
};

const isRowContactEmailCompleted = (
  dataRecipient: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
): boolean => {
  // First make sure we actually need to contact the vendor
  if (optOutInstruction?.doNotContact || dataRecipient.dataAccessibility === "NOT_ACCESSIBLE") {
    return true;
  }

  // Check if we have either an email or a default email
  return Boolean(dataRecipient.email || dataRecipient.defaultEmail);
};

export function AutomationConnectedBadge() {
  return (
    <div className="bg-green-100 rounded-[6px] py-[6px] px-[10px] flex items-center">
      <div>
        <BoltIcon className="w-[12px] h-[14px] text-green-600" />
      </div>
      <div className="text-green-600 text-[14px] font-medium ml-[8px]">Connected</div>
    </div>
  );
}
